<template>
  <v-form ref="form" lazy-validation class="ns-credit-card-form">
    <h2>
      <span class="title-small-bold">{{ t.cedynaFormTitle }}</span>
      <span class="secure"
        ><NsIcon icon-name="icon-lock" /> <span>{{ t.secure }}</span></span
      >
    </h2>
    <NsCreditCard
      v-model="value.cardNumber"
      :payment-type-id="value.paymentTypeId"
      :available-payments="paymentOptions"
      :translations="t.creditCard"
      @input="updateCardNumber"
      @paymentTypeId="updatePaymentTypeId"
    ></NsCreditCard>
  </v-form>
</template>

<script>
import { NsIcon } from '@nuskin/ns-icon';
import NsCreditCard from './NsCreditCard.vue';

/**
 * Original Author: Treg Anderson
 * Original Team: Mavericks
 *
 * NsCedynaForm is a simple component that imports NsCreditCard to create an
 * element for collecting cedyna card information. It is expected that this
 * "form" will be placed into a real form.
 *
 * This component takes in two props: translations and availablePayments. It
 * passes the needed translations to both NsCreditCard as well as
 * availablePayments to NsCreditCard.
 */
export default {
  name: 'NsCedynaForm',
  components: {
    NsCreditCard,
    NsIcon,
  },
  props: {
    /**
     * Available Payments for the market
     */
    availablePayments: {
      type: Array,
      default() {
        return [];
      },
    },

    value: {
      type: Object,
      default() {
        return {};
      },
    },

    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default() {
        return {
          cardNumLabel: 'Card Number',
          fieldRequired: 'Field is required',
          numbersNotAllowed: 'Numbers are not allowed',
          secure: 'Secure',
          formTitle: "Member's Card",
          creditCard: {
            fieldLabel: 'Card Number',
            fieldRequired: 'Field Required',
            notRecognizedCard: 'Your card is not recognized',
            placeholder: '               ',
          },
        };
      },
    },
  },
  data() {
    return {
      rules: [],
    };
  },

  computed: {
    t() {
      return this.translations;
    },
    paymentOptions() {
      return this.availablePayments;
    },
  },

  watch: {
    t() {
      if (Object.keys(this.t).length > 0) {
        this.initNameRules();
      }
    },
  },

  mounted() {},

  methods: {
    updateCardNumber(str) {
      this.$emit('input', { ...this.value, cardNumber: str });
    },
    updatePaymentTypeId(id) {
      this.$emit('input', { ...this.value, paymentTypeId: id });
    },
    initNameRules() {
      // No numbers or blank spaces
      this.rules = [
        (v) => (!!v && !!v.trim()) || this.t.fieldRequired,
        (v) => (!!v && !/[0-9]/.test(v.trim())) || this.t.numbersNotAllowed,
      ];
    },
    validate() {
      return this.$refs.form.validate();
    },
    reset() {
      this.$refs.form.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

::v-deep *:not(.v-icon) {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.ns-credit-card-form {
  h2 {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
  }

  .secure {
    display: flex;
    font-size: 14px;
    text-transform: uppercase;
    line-height: 24px;
    font-weight: map.get(typographyVariables.$typography, font-weight-semibold);

    .icon {
      height: 20px;
      margin-top: -1px;
    }

    span {
      padding-left: 5px;
    }
  }
}
</style>
